import { Component, OnInit, OnDestroy } from "@angular/core";
import { AuthenticationService } from "../services/authentication/authentication.service";
import { UsuarioService } from "../services/usuario/usuario.service";
import { Router } from "@angular/router";
import { switchMap, catchError } from "rxjs/operators";
import { EMPTY, Subject, Observable, timer, Subscription } from "rxjs";
import { MatDialog } from "@angular/material/dialog";
import { MatSnackBar } from "@angular/material/snack-bar";
import { WhatIsNewModalComponent } from "../what-is-new-modal/what-is-new-modal.component";
import { ChatIaService } from "../services/chat-ia/chat-ia.service";

@Component({
  selector: "app-side-menu",
  templateUrl: "./side-menu.component.html",
  styleUrls: ["./side-menu.component.scss"],
})
export class SideMenuComponent implements OnInit, OnDestroy {
  public unreadNotifications: number;
  public notifications: any = [];
  public notificationSwitch = false;

  private notificationQtySubscription: Subscription;
  private notificationQtySubject = new Subject<void>();

  private actualizarNotificationSubscription: Subscription;
  private actualizarNotificationSubject = new Subject<void>();

  private notificationSubscription: Subscription;
  private notificationSubject = new Subject<void>();

  private timerSubscription: Subscription;

  public chatIaHabilitado$ = this.chatIaService.habilitado();

  constructor(
    private authenticationService: AuthenticationService,
    private usuarioService: UsuarioService,
    private router: Router,
    private dialog: MatDialog,
    private toast: MatSnackBar,
    private chatIaService: ChatIaService,
  ) {}

  ngOnInit() {
    // Inicializar Streams.
    this.initQtyNotifications();
    this.initNotificationList();
    this.initActualizarNotification();

    // Inicializar Stream del Timer.
    const minutes = 1;
    this.timerSubscription = timer(0, minutes * 60000).subscribe(() => {
      this.notificationQtySubject.next();
    });
  }

  ngOnDestroy() {
    this.notificationSubscription.unsubscribe();
    this.notificationQtySubscription.unsubscribe();
    this.timerSubscription.unsubscribe();
  }

  /**
   * Obtener las notificaciones del usuario
   * y mostrarlas u ocultarlas
   */
  public initQtyNotifications() {
    this.notificationQtySubscription = this.notificationQtySubject
      .pipe(
        switchMap(() =>
          this.usuarioService.obtenerCantidadNotificaciones().pipe(
            catchError((err) => {
              console.error(err);
              return EMPTY;
            }),
          ),
        ),
      )
      .subscribe(
        (res: any) => {
          const actualNoLeidas = res["no_leidas"];
          this.unreadNotifications = actualNoLeidas;
          const almacenado = Number(localStorage.getItem("no_leidas") || "0");
          // Solo refrescar si hay novedades nuevas

          if (actualNoLeidas > almacenado) {
            localStorage.setItem("no_leidas", actualNoLeidas.toString());
            const almacenadoNuevo = Number(
              localStorage.getItem("no_leidas") || "0",
            );
            this.usuarioService.refreshSolicitudesSubject.next();
          } else if (actualNoLeidas !== almacenado) {
            localStorage.setItem("no_leidas", actualNoLeidas.toString());
          }
        },
        (err: any) => console.error(err),
      );

    this.notificationQtySubject.next();
  }

  public initActualizarNotification() {
    this.actualizarNotificationSubscription = this.actualizarNotificationSubject
      .pipe(
        switchMap(() =>
          this.usuarioService.actualizarNotificaciones().pipe(
            catchError((err) => {
              console.error(err);
              return EMPTY;
            }),
          ),
        ),
      )
      .subscribe(
        (res: any) => res,
        (err: any) => console.error(err),
      );
  }

  public initNotificationList() {
    this.notificationSubscription = this.notificationSubject
      .pipe(
        switchMap(() =>
          this.usuarioService.obtenerNotificaciones().pipe(
            catchError((err) => {
              console.error(err);
              return EMPTY;
            }),
          ),
        ),
      )
      .subscribe(
        (res: any) => (this.notifications = res),
        (err: any) => console.error(err),
      );
  }

  public obtenerNotifications(e) {
    // Obtener las notificaciones
    if (!this.notificationSwitch) {
      this.notificationSubject.next();
      this.actualizarNotificationSubject.next();
      this.unreadNotifications = 0;
    }

    // Mostrar u ocultar el DIV que las contiene
    this.notificationSwitch = !this.notificationSwitch;
    e.stopPropagation();
  }

  public closeNotificationWrap() {
    this.notificationSwitch = false;
  }

  /**
   * Solo rutas internas de la app (empiezan con "/" y no con "//"): la url la
   * arma el backend, pero no se navega a destinos externos.
   */
  public tieneEnlace(item: { url?: string | null }): boolean {
    const url = item && item.url;
    return !!url && url.startsWith("/") && !url.startsWith("//");
  }

  /** Abre la pantalla de la notificación (ej.: la grilla de insumos filtrada por los incorporados desde JDE). */
  public abrirNotificacion(item: { url?: string | null }, evento: Event) {
    if (!this.tieneEnlace(item)) {
      return;
    }
    evento.preventDefault();
    this.closeNotificationWrap();
    this.router.navigateByUrl(item.url);
  }

  public getUserFullName() {
    return this.usuarioService.getUserFullName();
  }

  public getUserRole() {
    return this.usuarioService.getUserRole();
  }

  public getUserLegajo() {
    return this.usuarioService.getUserLegajo();
  }

  // Antes hardcodeado al usuario 'FCORIA'; ahora depende del flag
  // puede_cambiar_rol, editable por usuario desde el ABM de usuarios.
  public puedeCambiarRol() {
    return this.usuarioService.puedeCambiarRol();
  }

  public alternarRolPropio() {
    this.usuarioService.alternarRolPropio().subscribe(
      // Los errores esperados (ej. sin permiso) llegan acá como "éxito" HTTP
      // 200 con el motivo en res.message — el backend los remapea así en
      // toda la app (response.error), no por status HTTP, así que hay que
      // distinguirlos por contenido antes de asumir que res.user es válido.
      (res: any) => {
        if (res && res.message) {
          this.toast.open(res.message, "Cerrar", { duration: 3000 });
          return;
        }
        this.authenticationService.actualizarUsuarioLocal(res);
        window.location.reload();
      },
      (err) => {
        console.error(err);
        this.toast.open("Error al intentar cambiar de rol.", "Cerrar", { duration: 3000 });
      },
    );
  }

  public categoryIsActive(category: string): boolean {
    if (this.router.url.includes(category)) {
      return true;
    }
    return false;
  }

  public itemIsActive(url: string): boolean {
    if (this.router.url === url) {
      return true;
    }
    return false;
  }

  public canSeeCategory(category: string): boolean {
    switch (category) {
      case "solicitudes":
        return (
          this.usuarioService.esSolicitante() ||
          this.usuarioService.esAdministrador() ||
          this.usuarioService.esResolutor() ||
          this.usuarioService.esLogistica() ||
          this.usuarioService.esCoordinadorLogistica()
        );
        break;
      case "configuracion":
        return this.usuarioService.esAdministrador();
        break;
      case "riego":
        return this.usuarioService.esSupervisorRiego();
        break;
      case "chat-ia":
        return (
          this.usuarioService.esSupervisorRiego() ||
          this.usuarioService.esAdministrador() ||
          this.usuarioService.esCoordinadorLogistica()
        );
        break;
      default:
        return false;
    }
  }

  public logout() {
    localStorage.setItem("no_leidas", "0");
    this.authenticationService.logOut();
  }

  public esSolicitante(): boolean {
    return this.usuarioService.esSolicitante();
  }

  public esAdministrador(): boolean {
    return this.usuarioService.esAdministrador();
  }

  public abrirNoticias() {
    this.dialog.open(WhatIsNewModalComponent);
  }
}
